import { useState } from 'react';
import { client } from '@/integrations/neon/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export function useDataManagement() {
  const [loading, setLoading] = useState(false);
  const { user, signOut } = useAuth();

  const exportDecisions = async (format: 'json' | 'csv') => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch all user data
      const { data: decisions, error: decisionsError } = await client
        .from('decisions')
        .select(`
          *,
          decision_tags(tag_id, tags(name)),
          decision_emotions(emotion_id, emotions(name)),
          rating_entries(*),
          scheduled_reviews(*)
        `)
        .eq('user_id', user.id);

      if (decisionsError) throw decisionsError;

      let dataStr: string;
      let filename: string;
      let mimeType: string;

      if (format === 'json') {
        dataStr = JSON.stringify(decisions, null, 2);
        filename = `decisionlog-export-${new Date().toISOString().split('T')[0]}.json`;
        mimeType = 'application/json';
      } else {
        // Convert to CSV
        if (!decisions || decisions.length === 0) {
          toast.error('No decisions to export');
          return;
        }

        const headers = ['Title', 'Description', 'Context', 'Rationale', 'Decision Date', 'Day 0 Rating', 'Review Intervals', 'Created At'];
        const csvData = decisions.map(decision => [
          decision.title,
          decision.description || '',
          decision.context || '',
          decision.rationale,
          decision.decision_date,
          decision.day0_rating,
          decision.review_intervals.join(';'),
          decision.created_at
        ]);

        dataStr = [headers, ...csvData].map(row => 
          row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        
        filename = `decisionlog-export-${new Date().toISOString().split('T')[0]}.csv`;
        mimeType = 'text/csv';
      }

      // Create and trigger download
      const blob = new Blob([dataStr], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Data exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const clearAllData = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // First get user's decision IDs
      const { data: userDecisions, error: decisionsError } = await client
        .from('decisions')
        .select('id')
        .eq('user_id', user.id);

      if (decisionsError) throw decisionsError;

      const decisionIds = userDecisions?.map(d => d.id) || [];

      // Delete in order to respect foreign key constraints
      if (decisionIds.length > 0) {
        await client.from('decision_emotions').delete().in('decision_id', decisionIds);
        await client.from('decision_tags').delete().in('decision_id', decisionIds);
        await client.from('rating_entries').delete().in('decision_id', decisionIds);
        await client.from('scheduled_reviews').delete().in('decision_id', decisionIds);
      }

      await client.from('decisions').delete().eq('user_id', user.id);
      await client.from('emotions').delete().eq('user_id', user.id);
      await client.from('tags').delete().eq('user_id', user.id);

      toast.success('All data cleared successfully');
    } catch (error) {
      console.error('Clear data error:', error);
      toast.error('Failed to clear data');
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // First clear all data
      await clearAllData();
      
      // Delete profile
      await client.from('profiles').delete().eq('user_id', user.id);
      
      // Delete user subscription
      await client.from('user_subscriptions').delete().eq('user_id', user.id);

      // Sign out and redirect
      await signOut();
      toast.success('Account deleted successfully');
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error('Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    exportDecisions,
    clearAllData,
    deleteAccount
  };
}